import { DEFAULT_OPTION, useAppForm } from '@massilia/features'
import {
  type BookingSessionResponse,
  countBillablePassengers,
  isExpiredSearchSessionError,
  toError,
  useGetMyPassengersQuery,
  useGetMyProfileQuery,
  useSubmitPassengers,
} from '@massilia/queries'
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { useNavigate } from '@tanstack/react-router'
import { differenceInYears, parse } from 'date-fns'
import { AlertTriangle, ChevronLeft, Info, Loader2, Mail } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { BookingSidebar } from '@/components/booking/booking-sidebar'
import { BookingStepActions } from '@/components/booking/booking-step-actions'
import { PromoCodeSlot } from '@/components/booking/promo-code-slot'
import { ReservationLoaderAnimation } from '@/components/booking/reservation-loader-animation'
import { TripHeader } from '@/components/booking/trip-header'
import { usePromoCode } from '@/components/booking/use-promo-code'
import {
  createInformationsSchema,
  type InformationsPassengerSchema,
} from '@/lib/schemas/informations'
import { useAuthStore } from '@/stores/auth.store'
import { usePricePreviewForSession } from '@/stores/booking-price-preview.store'
import { useFlightSearchStore } from '@/stores/flight-search.store'

import { getPassengerLabel, getPassengerType, getTotalPassengerCount } from '../-utils/constants'
import { PassengerFieldGroup } from './passenger-field-group'
import { toReservationError } from './reservation-error'
import { ReservationErrorCard } from './reservation-error-card'

interface InformationsFormProps {
  sessionId: string
  isPriceLoading: boolean
  session?: BookingSessionResponse
}

type ReservationError = ReturnType<typeof toReservationError>

export function InformationsForm({ sessionId, isPriceLoading, session }: InformationsFormProps) {
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuthStore()
  const { lastFlightResultsParams, lastFlightResultsRoute } = useFlightSearchStore()
  const [isNavigating, setIsNavigating] = useState(false)
  const [babyCompanionError, setBabyCompanionError] = useState<null | string>(null)
  // Échec de l'enregistrement de la réservation : affiché en ligne pour que le
  // formulaire (long à remplir) survive à une panne côté fournisseur.
  const [reservationError, setReservationError] = useState<null | ReservationError>(null)

  // Confirmation « Vous vous apprêtez à effectuer une réservation ». On ouvre la modale
  // depuis onSubmit (donc après validation du formulaire) et on attend la réponse de
  // l'utilisateur via une promesse résolue par les boutons de la modale.
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const confirmResolverRef = useRef<((confirmed: boolean) => void) | null>(null)

  const requestReservationConfirm = async (): Promise<boolean> =>
    new Promise((resolve) => {
      confirmResolverRef.current = resolve
      setIsConfirmOpen(true)
    })

  const resolveReservationConfirm = (confirmed: boolean) => {
    setIsConfirmOpen(false)
    confirmResolverRef.current?.(confirmed)
    confirmResolverRef.current = null
  }

  const isLocked = !!session?.bookingId

  // Aperçu du tarif déposé au clic sur l'offre : il tient la sidebar le temps que le
  // price Affidium réponde. Décoratif uniquement — les bornes de validation ci-dessous
  // restent tirées de `session.priceData`, donc de la donnée confirmée.
  const pricePreview = usePricePreviewForSession(sessionId)

  const {
    isVerifying: isPromoCodePending,
    promoCodeError,
    remove: removePromoCode,
    verify: verifyPromoCode,
  } = usePromoCode(sessionId)

  const promoCodeDiscount = session?.promoCode ? session.promoCode.discountCents / 100 : null
  const billablePassengerCount = countBillablePassengers(session?.flightDetails.passengersCount)

  const { data: savedPassengers } = useGetMyPassengersQuery(isAuthenticated)
  const { data: profile } = useGetMyProfileQuery(isAuthenticated)
  // throwOnError: false — les échecs sont rendus en ligne (ReservationErrorCard).
  // Seule une session expirée est renvoyée à la boundary, qui propose de relancer
  // la recherche : à ce stade le formulaire n'a plus rien à sauver.
  const { isPending: isSubmitting, mutateAsync: submitPassengers } = useSubmitPassengers(
    sessionId,
    { throwOnError: isExpiredSearchSessionError }
  )

  // Phone is stored on Client.phoneNumbers[] (multiple numbers w/ types). Prefer the
  // PRINCIPAL one, fallback to the first available.
  const accountPhone =
    profile?.client?.phoneNumbers?.find((p) => p.type === 'PRINCIPAL')?.number ??
    profile?.client?.phoneNumbers?.[0]?.number ??
    ''

  const passengersCount = (session?.flightDetails.passengersCount ?? {}) as {
    adults?: number
    babies?: number
    infants?: number
    seniors?: number
    youngsters?: number
  }

  const resolvedPassengersCount = {
    adults: passengersCount.adults ?? 0,
    babies: passengersCount.babies ?? 0,
    infants: passengersCount.infants ?? 0,
    seniors: passengersCount.seniors ?? 0,
    youngsters: passengersCount.youngsters ?? 0,
  }

  const firstJourney = session?.priceData?.journeys?.at(0)
  const firstSegment = firstJourney?.segments?.at(0)
  const firstFlightDepartureDate = firstSegment?.departure?.rawDate ?? ''

  const lastJourney = session?.priceData?.journeys?.at(-1)
  const lastSegment = lastJourney?.segments?.at(-1)
  const lastFlightArrivalDate = lastSegment?.arrival?.rawDate ?? ''

  const defaultPassengers = useMemo(
    () =>
      session?.passengers && session.passengers.length > 0
        ? session.passengers.map((p) => {
            const [year = '', month = '', day = ''] = p.birthDate?.split('-') ?? []
            return {
              firstName: p.firstName,
              lastName: p.lastName,
              savedPassengerId: null,
              birthDay: day,
              birthMonth: month,
              birthYear: year,
              fidelityCard: p.fidelityCard
                ? {
                    companyName: {
                      label: p.fidelityCard.companyName,
                      value: p.fidelityCard.companyName,
                    },
                    cardNumber: p.fidelityCard.cardNumber,
                  }
                : undefined,
              gender: p.gender,
              identityDocument: p.identityDocument
                ? {
                    documentNumber: p.identityDocument.documentNumber,
                    expiryDate: p.identityDocument.expiryDate,
                    issuingCountry: {
                      label: p.identityDocument.issuingCountry,
                      value: p.identityDocument.issuingCountry,
                    },
                    type: p.identityDocument.type,
                  }
                : undefined,
              nationality: p.nationality
                ? { label: p.nationality, value: p.nationality }
                : DEFAULT_OPTION,
              retrieveFromSaved: false,
            }
          })
        : createDefaultPassengers(
            (session?.flightDetails.passengersCount ?? {}) as {
              adults?: number
              babies?: number
              infants?: number
              seniors?: number
              youngsters?: number
            }
          ),
    [session]
  )

  const form = useAppForm({
    defaultValues: {
      buyerEmail: session?.buyerEmail ?? user?.email ?? '',
      acceptsDepositCommitment: isLocked,
      acceptsTermsAndConditions: isLocked,
      buyerPhone: session?.buyerPhone ?? accountPhone ?? '',
      passengers: defaultPassengers,
    },
    listeners: {
      onChange: ({ fieldApi }) => {
        if (/^passengers\[\d+\]\.birth(Day|Month|Year)$/.test(fieldApi.name)) {
          setBabyCompanionError(null)
        }
      },
    },
    onSubmit: async ({ value }) => {
      // Validation OK : on demande confirmation avant de créer la réservation.
      const confirmed = await requestReservationConfirm()
      if (!confirmed) return

      const passengers = value.passengers.map((p, index) => ({
        firstName: p.firstName,
        lastName: p.lastName,
        birthDate: `${p.birthYear}-${p.birthMonth.padStart(2, '0')}-${p.birthDay.padStart(2, '0')}`,
        fidelityCard: p.fidelityCard
          ? {
              companyName: p.fidelityCard.companyName.label,
              cardNumber: p.fidelityCard.cardNumber,
            }
          : undefined,
        gender: p.gender,
        identityDocument: p.identityDocument
          ? {
              documentNumber: p.identityDocument.documentNumber,
              expiryDate: p.identityDocument.expiryDate,
              issuingCountry: p.identityDocument.issuingCountry.value,
              type: p.identityDocument.type,
            }
          : undefined,
        nationality: p.nationality.value,
        type: getPassengerType(resolvedPassengersCount, index),
      }))

      try {
        setReservationError(null)
        await submitPassengers({
          buyerEmail: value.buyerEmail.trim(),
          buyerPhone: value.buyerPhone.trim(),
          passengers,
        })
      } catch (error) {
        // Session expirée : déjà relayée à la boundary par react-query
        // (throwOnError), qui propose de relancer la recherche.
        const failure = toError(error)
        if (!isExpiredSearchSessionError(failure)) {
          setReservationError(toReservationError(failure))
        }
        return
      }

      setIsNavigating(true)
      void navigate({
        search: { session: sessionId },
        to: '/assurances',
      })
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change',
    }),
    validators: {
      onDynamic: ({ value }) => {
        const schema = createInformationsSchema({
          firstFlightDepartureDate,
          lastFlightArrivalDate,
          passengersCount: resolvedPassengersCount,
        })
        return standardSchemaValidators.validate({ validationSource: 'form', value }, schema)
      },
    },
  })

  // Profile fetch is async — if it arrives after the form has mounted with an empty
  // phone, hydrate the field. Don't overwrite a value the user has already typed
  // or one already saved on the session.
  useEffect(() => {
    if (!accountPhone) return
    if (session?.buyerPhone) return
    if (form.state.values.buyerPhone) return
    form.setFieldValue('buyerPhone', accountPhone)
  }, [accountPhone, session?.buyerPhone, form])

  const sidebar = (
    <BookingSidebar
      isPriceLoading={isPriceLoading}
      preview={pricePreview}
      promoCodeDiscount={promoCodeDiscount}
      promoCodeSlot=<PromoCodeSlot
        appliedCode={session?.promoCode?.code ?? null}
        discountEuros={promoCodeDiscount}
        error={promoCodeError}
        isVerifying={isPromoCodePending}
        onRemove={() => {
          void removePromoCode()
        }}
        onVerify={(code) => {
          void verifyPromoCode(code)
        }}
        passengerCount={billablePassengerCount}
      />
      session={session}
    />
  )

  return (
    <form
      className="flex w-full flex-col gap-8 px-4 py-8 md:px-28 lg:flex-row lg:gap-12"
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()

        if (isLocked) {
          setIsNavigating(true)
          void navigate({ search: { session: sessionId }, to: '/assurances' })
          return
        }

        // Check baby companion constraint for banner display
        const passengers = form.state.values.passengers
        let nbBabies = 0
        let nbAdults18Plus = 0
        const departureDate = parse(firstFlightDepartureDate, 'yyyy-MM-dd', new Date())
        for (const p of passengers) {
          if (p.birthYear && p.birthMonth && p.birthDay) {
            const birthDate = parse(
              `${p.birthYear}-${p.birthMonth}-${p.birthDay}`,
              'yyyy-MM-dd',
              new Date()
            )
            const age = differenceInYears(departureDate, birthDate)
            if (age < 2) nbBabies++
            if (age >= 18) nbAdults18Plus++
          }
        }
        if (nbBabies > 0 && nbBabies > nbAdults18Plus) {
          setBabyCompanionError(
            `Chaque bébé doit être accompagné par un adulte de 18 ans ou plus. Il y a ${nbBabies} bébé(s) pour ${nbAdults18Plus} adulte(s) éligible(s).`
          )
          return
        }

        setBabyCompanionError(null)
        void form.handleSubmit()
      }}
    >
      {/* Right column — Sidebar (desktop) */}
      <div className="hidden w-full shrink-0 lg:order-last lg:block lg:w-96">{sidebar}</div>

      {/* Left column — Form */}
      <div className="flex flex-1 flex-col gap-9">
        {/* Récap voyage + échéancier, en tête sur mobile comme sur desktop */}
        {session && <TripHeader session={session} />}

        {/* Lock banner shown after the booking has been registered with Affidium */}
        {isLocked && (
          <div className="flex items-start gap-3 rounded-lg border border-brand-100 bg-brand-50 p-3">
            <Info className="mt-0.5 size-4 shrink-0 text-brand-600" />
            <p className="text-sm text-gray-light-900">
              Votre réservation a été enregistrée. Pour modifier les informations passagers ou les
              coordonnées, contactez l&apos;agence.
            </p>
          </div>
        )}

        {/* Section: Vos coordonnées */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-semibold text-gray-light-900">Vos coordonnées</h2>
            <p className="text-sm text-gray-light-900">
              Ces informations nous permettent de finaliser votre réservation et de vous contacter.
            </p>
          </div>
          <div
            className={cn(
              'flex flex-col gap-4 md:flex-row md:items-end',
              isLocked &&
                '[&_:disabled]:opacity-100 [&_input:disabled]:bg-gray-light-50 [&_input:disabled]:text-gray-light-900'
            )}
          >
            <div className="flex-1">
              <form.AppField name="buyerEmail">
                {(field) => (
                  <field.TextField
                    icon={Mail}
                    label="Adresse email"
                    placeholder="votre@email.com"
                    disabled={isLocked}
                    type="email"
                  />
                )}
              </form.AppField>
            </div>
            <div className="flex-1">
              <form.AppField name="buyerPhone">
                {(field) => <field.PhoneNumberField label="Téléphone" disabled={isLocked} />}
              </form.AppField>
            </div>
          </div>
        </div>

        {/* Section: Qui sont les voyageurs ? */}
        <div className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold text-gray-light-900">Qui sont les voyageurs ?</h2>
          <p className="text-sm text-gray-light-900">
            Les noms des voyageurs doivent être identiques à ceux indiqués sur vos cartes
            d&apos;identité ou passeports
          </p>
        </div>

        {/* Baby companion error banner */}
        {babyCompanionError && (
          <div className="flex items-start gap-3 rounded-lg border border-danger/30 bg-danger-subtle p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-danger" />
            <p className="text-sm text-danger-foreground">{babyCompanionError}</p>
          </div>
        )}

        {/* Passenger field groups — fully disabled once the booking is locked */}
        <fieldset
          className={cn(
            'flex flex-col gap-9',
            // Mode verrouillé : lecture seule, pas fantôme — les valeurs restent à
            // pleine force pour être relues, seul le fond signale l'inertie.
            isLocked &&
              'pointer-events-none [&_:disabled]:opacity-100 [&_input:disabled]:bg-gray-light-50 [&_input:disabled]:text-gray-light-900 [&_button:disabled]:bg-gray-light-50'
          )}
          disabled={isLocked}
        >
          <form.AppField mode="array" name="passengers">
            {(passengersField) =>
              passengersField.state.value.map((_, index) => (
                <PassengerFieldGroup
                  fields={{
                    firstName: `passengers[${index}].firstName`,
                    lastName: `passengers[${index}].lastName`,
                    savedPassengerId: `passengers[${index}].savedPassengerId`,
                    birthDay: `passengers[${index}].birthDay`,
                    birthMonth: `passengers[${index}].birthMonth`,
                    birthYear: `passengers[${index}].birthYear`,
                    fidelityCard: `passengers[${index}].fidelityCard`,
                    gender: `passengers[${index}].gender`,
                    identityDocument: `passengers[${index}].identityDocument`,
                    nationality: `passengers[${index}].nationality`,
                    retrieveFromSaved: `passengers[${index}].retrieveFromSaved`,
                  }}
                  form={form}
                  index={index}
                  key={index}
                  label={getPassengerLabel(resolvedPassengersCount, index)}
                  savedPassengers={savedPassengers}
                />
              ))
            }
          </form.AppField>
        </fieldset>

        {/* Consentements (CGV + engagement acompte) */}
        <div className="flex flex-col gap-3">
          <form.AppField name="acceptsTermsAndConditions">
            {(field) => (
              <field.CheckboxField
                className="size-5 [&_svg]:size-4"
                containerClassName="items-start"
                disabled={isLocked}
                label={
                  <span>
                    J&apos;accepte les{' '}
                    <a
                      className="font-medium text-brand-700 underline"
                      href="https://www.massiliavoyages.fr/conditions-generales-vente-vols-secs/"
                      rel="noreferrer"
                      target="_blank"
                    >
                      conditions générales de vente
                    </a>
                  </span>
                }
              />
            )}
          </form.AppField>
          <form.AppField name="acceptsDepositCommitment">
            {(field) => (
              <field.CheckboxField
                className="size-5 [&_svg]:size-4"
                containerClassName="items-start"
                disabled={isLocked}
                label="Je souhaite réserver et m'engage à verser l'acompte ou la totalité du billet dans les délais indiqués dans la prochaine étape"
              />
            )}
          </form.AppField>
        </div>

        {/* Échec de la réservation côté fournisseur — le formulaire reste intact */}
        {reservationError && (
          <ReservationErrorCard message={reservationError.message} type={reservationError.type} />
        )}

        {/* Mobile : détail du tarif consultable en fin d'étape */}
        <div className="lg:hidden">{sidebar}</div>

        {/* Navigation buttons */}
        <BookingStepActions>
          <Button
            className="gap-2"
            disabled={isSubmitting || isNavigating}
            onClick={() => {
              if (lastFlightResultsParams) {
                void navigate({
                  search: lastFlightResultsParams,
                  to: lastFlightResultsRoute ?? '/vols/resultats',
                })
              } else {
                void navigate({ to: '/recherche' })
              }
            }}
            type="button"
            variant="ghost"
          >
            <ChevronLeft className="size-4 text-brand-700" />
            <span className="text-sm font-medium text-brand-700">Étape précédente</span>
          </Button>
          <Button
            className="min-w-44 gap-2 bg-brand-600 px-6 hover:bg-brand-700"
            disabled={isSubmitting || isNavigating}
            type="submit"
          >
            {isLocked && isNavigating ? (
              <Loader2 className="size-4 animate-spin text-white" />
            ) : (
              <span className="text-sm font-medium text-white">
                {isLocked ? 'Continuer' : 'Je réserve'}
              </span>
            )}
          </Button>
        </BookingStepActions>
      </div>

      {/* Confirmation de réservation */}
      <Dialog
        onOpenChange={(open) => {
          // Fermeture via overlay / échap / croix = annulation.
          if (!open) resolveReservationConfirm(false)
        }}
        open={isConfirmOpen}
      >
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Vous vous apprêtez à effectuer une réservation.</DialogTitle>
            <DialogDescription>Souhaitez-vous continuer&nbsp;?</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row gap-3">
            <Button
              className="flex-1"
              onClick={() => {
                resolveReservationConfirm(false)
              }}
              type="button"
              variant="outline"
            >
              Annuler
            </Button>
            <Button
              className="flex-1 bg-brand-600 hover:bg-brand-700"
              onClick={() => {
                resolveReservationConfirm(true)
              }}
              type="button"
            >
              Confirmer la réservation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loader bloquant pendant la création de la réservation puis la navigation */}
      <Dialog open={!isLocked && (isSubmitting || isNavigating)}>
        <DialogContent
          className="sm:max-w-sm"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          showCloseButton={false}
        >
          <DialogHeader className="items-center">
            <ReservationLoaderAnimation />
            <DialogTitle>Réservation en cours…</DialogTitle>
            <DialogDescription>Merci de patienter, ne quittez pas cette page.</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </form>
  )
}

function createDefaultPassengers(
  passengers: Record<string, number | undefined>
): InformationsPassengerSchema[] {
  const total = getTotalPassengerCount({
    adults: passengers.adults ?? 0,
    babies: passengers.babies ?? 0,
    infants: passengers.infants ?? 0,
    seniors: passengers.seniors ?? 0,
    youngsters: passengers.youngsters ?? 0,
  })
  return Array.from({ length: total }, () => ({
    firstName: '',
    lastName: '',
    savedPassengerId: null,
    birthDay: '',
    birthMonth: '',
    birthYear: '',
    fidelityCard: undefined,
    gender: '' as 'FEMALE' | 'MALE',
    identityDocument: undefined,
    nationality: DEFAULT_OPTION,
    retrieveFromSaved: false,
  }))
}
